<script setup>
import { computed } from 'vue'
import { 
  SelectContent, 
  SelectIcon, 
  SelectItem, 
  SelectRoot, 
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger, 
  SelectValue,
  SelectViewport
} from 'reka-ui'
import { ChevronDown, ChevronUp, Check } from 'lucide-vue-next'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  placeholder: {
    type: String,
    default: 'Select an option...'
  },
  options: {
    type: Array,
    required: true,
    // Expected format: [{ value: 'val', label: 'Label', disabled?: boolean }]
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'ghost', 'outline'].includes(value)
  },
  loading: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'clear'])

const handleValueChange = (value) => {
  if (value === null) return
  const option = props.options.find(opt => opt.value === value) || null
  emit('update:modelValue', value)
  emit('change', value, option)
}

const handleClear = (event) => {
  event.stopPropagation()
  emit('update:modelValue', null)
  emit('change', null, null)
  emit('clear')
}

const selectedOption = computed(() => {
  return props.options.find(option => option.value === props.modelValue)
})

const displayValue = computed(() => {
  return selectedOption.value?.label || props.placeholder
})

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  }
  return sizes[props.size]
})

const variantClasses = computed(() => {
  const variants = {
    default: 'bg-white border-gray-300 hover:border-gray-400',
    ghost: 'bg-transparent border-transparent hover:bg-gray-50',
    outline: 'bg-transparent border-gray-300 hover:border-primary'
  }
  return variants[props.variant]
})

const triggerClasses = computed(() => {
  return [
    'w-full border rounded-base focus:ring-2 transition-colors',
    'flex items-center justify-between',
    'data-[placeholder]:text-gray-500 text-left',
    'focus-visible:outline-none focus-visible:ring-2',
    sizeClasses.value,
    variantClasses.value,
    props.error 
      ? 'border-danger focus:border-danger focus:ring-danger-muted focus-visible:ring-danger-muted' 
      : 'focus:border-primary focus:ring-primary-muted focus-visible:ring-primary-muted',
    props.disabled 
      ? 'opacity-50 cursor-not-allowed bg-gray-50' 
      : '',
    props.loading ? 'cursor-wait' : ''
  ].filter(Boolean).join(' ')
})
</script>

<template>
  <SelectRoot 
    :model-value="modelValue" 
    @update:model-value="handleValueChange"
    :disabled="disabled || loading"
  >
    <SelectTrigger :class="triggerClasses">
      <SelectValue 
        :placeholder="placeholder"
        class="text-left truncate flex-1"
      >
        {{ displayValue }}
      </SelectValue>
      
      <div class="flex items-center gap-1 ml-2 flex-shrink-0">
        <!-- Clear button -->
        <button
          v-if="clearable && modelValue && !disabled && !loading"
          type="button"
          @click="handleClear"
          class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600 rounded-sm hover:bg-gray-100 transition-colors"
          aria-label="Clear selection"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>

        <!-- Loading spinner -->
        <div
          v-if="loading"
          class="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin"
          aria-label="Loading"
        />

        <!-- Chevron icon -->
        <SelectIcon v-else>
          <ChevronDown 
            :size="16" 
            class="text-gray-400 transition-transform duration-200 data-[state=open]:rotate-180"
          />
        </SelectIcon>
      </div>
    </SelectTrigger>

    <SelectContent 
      position="popper"
      side="bottom"
      align="start"
      :side-offset="4"
      class="bg-white border border-gray-200 rounded-base shadow-base z-50 overflow-hidden min-w-[var(--reka-select-trigger-width)]"
      :style="{ width: 'var(--reka-select-trigger-width)', maxHeight: 'var(--reka-select-content-available-height)' }"
    >
      <SelectScrollUpButton class="flex items-center justify-center h-6 bg-white text-gray-400 hover:text-gray-600 cursor-default">
        <ChevronUp :size="12" />
      </SelectScrollUpButton>

      <SelectViewport class="max-h-60">
        <SelectItem 
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
          class="relative flex items-center px-3 py-2 text-sm cursor-pointer outline-none select-none data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed data-[highlighted]:bg-primary-light data-[highlighted]:text-primary data-[state=checked]:bg-primary-light data-[state=checked]:text-primary data-[state=checked]:font-medium hover:bg-gray-50 transition-colors"
        >
          <span class="truncate pr-6">{{ option.label }}</span>
          
          <!-- Checkmark for selected item -->
          <span 
            v-if="option.value === modelValue"
            class="absolute right-3 flex items-center text-primary"
          >
            <Check :size="14" />
          </span>
        </SelectItem>
      </SelectViewport>

      <SelectScrollDownButton class="flex items-center justify-center h-6 bg-white text-gray-400 hover:text-gray-600 cursor-default">
        <ChevronDown :size="12" />
      </SelectScrollDownButton>
    </SelectContent>
  </SelectRoot>
</template>